import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { useSelector } from "react-redux";

const StyledLink = styled(Link)(({ theme }) => ({
  alignItems: "center",
  color: theme.palette.common.black,
  cursor: "pointer",
  display: "flex",
  overflow: "hidden",
  textDecoration: "underline",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

/**
 * The creator named or described, but not offered as a link.
 *
 * Used when there is no profile to open: either the Individuals store has nothing for this person,
 * or what it has is a name the API sent without a profile behind it. Underlining it would advertise
 * a dialog that cannot be filled.
 */
const StyledDescription = styled("span")({
  alignItems: "center",
  display: "flex",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

/**
 * What the cell reads while the creator's individual is still being fetched, and for a creator this
 * viewer is not entitled to see named.
 *
 * The same shape as the client-team roster's "Client team member" (see
 * `proposal-client-team-view.tsx`): it says what the person is to this proposal rather than
 * claiming their name is missing. "Unknown user" read as a fault, when in fact the row knows
 * exactly who created it — the marketplace directory just does not answer for them here.
 */
const UNRESOLVED_CREATOR_DESCRIPTION = "Proposal creator";

type CreatorLinkProps = {
  creator: EntityMember;
};

/**
 * Names a proposal's creator from the Individuals store, which is the one place their name lives.
 *
 * Deliberately reads and never fetches. Hydration is the table's job — `proposals-page.tsx` asks
 * for every creator the page is missing in one lookup, the way `proposal-client-team-view.tsx`
 * hydrates a whole roster — because a component that fetched for itself would put one request on
 * the wire per row. Reading the store rather than holding a copy of the name is also what keeps
 * the cell current: an edited profile lands in the store and the cell re-renders with it.
 */
export default function CreatorLink(props: Readonly<CreatorLinkProps>) {
  const { creator } = props;

  const individual = useSelector(getIndividualByUserId(creator.userId));

  const { openDialog, closeDialog } = useDialog();

  function openProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      contentSxProps: {
        display: "flex",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
      titleStyle: {
        paddingBottom: 0
      },
      onClose: closeDialog,
    });
  }

  if (!individual) {
    return (
      <StyledDescription title={UNRESOLVED_CREATOR_DESCRIPTION}>
        {UNRESOLVED_CREATOR_DESCRIPTION}
      </StyledDescription>
    );
  }

  // A contact the API sent because the viewer is connected to this person, whose marketplace
  // profile is not published. There is a name to show and no profile to open, so the cell says who
  // they are without offering a dialog that would come back with the name again (#5538).
  if (individual.contactOnly) {
    return <StyledDescription title={individual.fullName}>{individual.fullName}</StyledDescription>;
  }

  return (
    <StyledLink
      onClick={(event) => {
        event.stopPropagation();
        openProfileDialog(individual.id);
      }}
    >
      {`${individual.fullName}`}
    </StyledLink>
  );
}
